import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, RotateCcw, Upload } from 'lucide-react';
import { clsx } from 'clsx';
import { Button, EmptyState, MetricCard, useToast } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { VerificationStatus } from '../api/verificationApi';
import { useWorklist } from '../hooks/useWorklist';
import { useWorklistMetrics } from '../hooks/useWorklistMetrics';
import { useConnections, useRerunFailed, useSyncOpenDental } from '../hooks/useConnections';
import { useCanEdit } from '../hooks/useCanEdit';
import { AttentionCards } from '../components/AttentionCards';
import { WorklistTable } from '../components/WorklistTable';
import { IV_BASE, IvSubnav } from '../components/IvSubnav';
import { formatHours, formatPercent, getErrorMessage, STATUS_FILTERS } from '../utils/format';
import styles from './WorklistPage.module.css';

type Filter = VerificationStatus | 'all';

export function WorklistPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>('all');

  const status = filter === 'all' ? undefined : filter;
  const { rows, isLoading, isError, error, refetch, locationId } = useWorklist(status);
  const { rows: allRows, isLoading: allLoading } = useWorklist();
  const canEdit = useCanEdit();
  // Until the location resolves (agency impersonation sets it asynchronously)
  // the disabled query reports "not loading" with no rows — that is not "empty".
  const waitingForLocation = !locationId;
  const { metrics } = useWorklistMetrics();
  const { connections } = useConnections();
  const sync = useSyncOpenDental();
  const rerunFailed = useRerunFailed();
  const retryable = (metrics?.failed ?? 0) + (metrics?.awaitingProduction ?? 0);

  function runRerunFailed() {
    rerunFailed.mutate(undefined, {
      onSuccess: (r) =>
        toast.success(`${r.queued} check${r.queued === 1 ? '' : 's'} re-queued${r.skipped ? ` · ${r.skipped} skipped (still missing details)` : ''}`),
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't re-run the failed checks")),
    });
  }

  const openDentalConnected = !!connections?.openDental.configured;
  const nothingAtAll = !waitingForLocation && !allLoading && allRows.length === 0 && filter === 'all';

  function runSync() {
    sync.mutate(undefined, {
      onSuccess: (r) => {
        toast.success(
          `Open Dental sync: ${r.fetched} fetched · ${r.created} new · ${r.updated} updated · ${r.checksQueued} checks started` +
            (r.skippedNoInsurance > 0 ? ` · ${r.skippedNoInsurance} skipped (no insurance)` : ''),
        );
      },
      onError: (err) => toast.error(getErrorMessage(err, 'Sync failed — check the connection')),
    });
  }

  return (
    <div className={patterns.page}>
      <IvSubnav />

      <section className={styles.section}>
        <div className={patterns.sectionLabel}>What needs my attention?</div>
        {metrics ? (
          <AttentionCards
            metrics={metrics}
            onReview={() => setFilter('needs_review')}
            onFix={() => setFilter('failed')}
          />
        ) : (
          <div className={clsx(patterns.panelCompact, patterns.cellMuted)}>Loading…</div>
        )}
      </section>

      <section className={styles.section}>
        <div className={patterns.sectionLabel}>This week</div>
        <p className={patterns.sectionLabelSub}>
          Verifications run automatically when an appointment is booked. Hours saved is versus
          manual portal + phone work.
        </p>
        <div className={patterns.statGrid4}>
          <MetricCard
            title="Verified this week"
            value={metrics?.verifiedThisWeek ?? '—'}
            sub={metrics ? `of ${metrics.upcomingThisWeek} upcoming appointments` : undefined}
          />
          <MetricCard
            title="Awaiting your review"
            value={metrics?.awaitingReview ?? '—'}
            tone={metrics && metrics.awaitingReview > 0 ? 'yellow' : 'default'}
            sub="finish before the AM huddle"
          />
          <MetricCard
            title="Hours saved this month"
            value={metrics ? formatHours(metrics.hoursSavedThisMonth) : '—'}
            tone="green"
            sub="portal + hold time avoided"
          />
          <MetricCard
            title="Clean-claim readiness"
            value={metrics ? formatPercent(metrics.cleanClaimReadinessPct) : '—'}
            sub="structured fields written to PMS"
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.tableHeader}>
          <div>
            <div className={patterns.sectionLabel}>Upcoming appointments</div>
            <p className={patterns.sectionLabelSub}>
              From Open Dental sync and imported schedules. Click any patient to see the full
              benefit breakdown and where each detail came from.
            </p>
          </div>
          <div className={styles.headerActions}>
            {canEdit && retryable > 0 && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<RotateCcw size={14} aria-hidden />}
                onClick={runRerunFailed}
                isLoading={rerunFailed.isPending}
                title={
                  metrics?.awaitingProduction
                    ? `${metrics.failed} failed + ${metrics.awaitingProduction} awaiting production`
                    : undefined
                }
              >
                Re-run {retryable} {retryable === 1 ? 'check' : 'checks'}
              </Button>
            )}
            {canEdit && openDentalConnected && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<RefreshCw size={14} aria-hidden />}
                onClick={runSync}
                isLoading={sync.isPending}
              >
                Sync Open Dental
              </Button>
            )}
            {canEdit && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Upload size={14} aria-hidden />}
                onClick={() => navigate(`${IV_BASE}/import`)}
              >
                Import schedule
              </Button>
            )}
          </div>
        </div>

        {nothingAtAll ? (
          <div className={patterns.panel}>
            <EmptyState
              title="No appointments yet"
              description="Import a schedule or connect Open Dental to start verifying benefits."
              actions={
                <div className={styles.emptyActions}>
                  <Button onClick={() => navigate(`${IV_BASE}/import`)}>Import schedule</Button>
                  <Button variant="secondary" onClick={() => navigate(`${IV_BASE}/connections`)}>
                    Connections
                  </Button>
                </div>
              }
            />
          </div>
        ) : (
          <>
            <div className={patterns.filterGroup} role="tablist" aria-label="Filter by status">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.value}
                  className={clsx(
                    patterns.filterPill,
                    styles.filterPill,
                    filter === f.value && patterns.filterPillActive,
                  )}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {isError ? (
              <div className={patterns.panel}>
                <EmptyState
                  compact
                  title="Couldn't load the worklist"
                  description={getErrorMessage(error, 'Try again in a moment.')}
                  action={{ label: 'Retry', onClick: () => refetch() }}
                />
              </div>
            ) : (
              <WorklistTable rows={rows} isLoading={isLoading || waitingForLocation} />
            )}
          </>
        )}
      </section>
    </div>
  );
}
